/**
 * Utility to determine firmware upgrade status from a device's meta object.
 *
 * For some brands (e.g. Rosslare), `upgradeRequired` and `upgradeAvailable`
 * live inside `meta.extensions[]` rather than at the top level of `meta`.
 * This helper checks both locations so every UI surface picks up the status.
 */

interface DeviceMetaExtension {
  upgradeRequired?: boolean | null;
  upgradeAvailable?: boolean | null;
}

interface DeviceMetaLike {
  upgradeRequired?: boolean | null;
  upgradeAvailable?: boolean | null;
  extensions?: DeviceMetaExtension[];
}

export interface FirmwareUpgradeStatus {
  upgradeRequired: boolean;
  upgradeAvailable: boolean;
  hasUpgrade: boolean;
}

/**
 * Resolves firmware upgrade status by checking both top-level `meta` fields
 * and nested `meta.extensions[]` entries.
 *
 * Priority: `upgradeRequired` takes precedence over `upgradeAvailable`.
 */
export const getDeviceFirmwareUpgradeStatus = (meta?: DeviceMetaLike | null): FirmwareUpgradeStatus => {
  if (!meta) {
    return { upgradeRequired: false, upgradeAvailable: false, hasUpgrade: false };
  }

  const extensions = Array.isArray(meta.extensions) ? meta.extensions : [];

  const upgradeRequired =
    meta.upgradeRequired === true || extensions.some((ext) => ext.upgradeRequired === true);

  const upgradeAvailable =
    meta.upgradeAvailable === true || extensions.some((ext) => ext.upgradeAvailable === true);

  return {
    upgradeRequired,
    upgradeAvailable,
    hasUpgrade: upgradeRequired || upgradeAvailable,
  };
};

